import { getUserPermission } from '../apicalls/employeeMaster';
import { TAX_RULE } from '../const/Const';
import { TGetUserPermission } from '../types/common';
import axios, { AxiosResponse } from 'axios';
import heic2any from 'heic2any';
import React from 'react';
import { getConstructions } from '../apicalls/construction';
import { TSelectItem } from '../types/common';
import {
  CURRENCY_UNITS,
  ACCEPTABLE_FILE_TYPES,
  ACCEPTABLE_PICTURE_TYPES
} from '../const/Const';

export function currencyStyle(num: number, currency: number) {
  if (currency === CURRENCY_UNITS.COMMA) {
    return String(num).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,') + ' 円';
  } else if (currency === CURRENCY_UNITS.THOUSAND_YEN) {
    return (
      String(Math.round(num / 1000)).replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        '$1,'
      ) + ' 千円'
    );
  } else if (currency === CURRENCY_UNITS.MILLION_YEN) {
    return (
      String(Math.round(num / 1000000)).replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        '$1,'
      ) + ' 百万円'
    );
  } else if (currency === CURRENCY_UNITS.TEN_THOUSAND_YEN) {
    return String(Math.round(num / 10000)) + ' 万円';
  } else {
    return String(num) + ' 円';
  }
}

export const loginCheck = (
  setIsLogin: React.Dispatch<React.SetStateAction<boolean>>,
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>
) => {
  // バックエンドのソースには「get_user_roles」と記載のあるエンドポイントへアクセス
  getUserPermission().then(
    (res: AxiosResponse<TGetUserPermission | string>) => {
      console.log(res);
      const data: TGetUserPermission | string = res.data;
      // 未ログイン状態の場合
      if (data === 'Not logged in!') {
        setIsLogin(false);
      } else {
        // エラーの場合
        if (typeof data === 'string') {
          setIsLogin(false);
          setErrorMessage(data);
        } else {
          // エラーの場合
          if (data.error) {
            setIsLogin(false);
            setErrorMessage(data.error);

            // ログイン状態の場合
          } else {
            setIsLogin(true);
          }
        }
      }
    }
  );
};

/**
 * 資料登録
 * @param data リクエストパラメータ
 */
export const createFile = (data: any) => {
  return axios
    .post(`/api/v1/create/file`, data, {
      headers: {
        withCredentials: true,
        'Content-Type': 'multipart/form-data'
      }
    })
    .then(function (response) {
      return response.data;
    })
    .catch(err => {
      if (err.response.data) {
        if (
          err.response.data.error &&
          err.response.data.error === 'Missing cookie access_token_cookie'
        ) {
          return 'Not logged in!';
        } else return err.response.data;
      } else return err.response;
    });
};

/**
 * 写真登録
 * @param data リクエストパラメータ
 */
export const createPictureByUser = (data: any) => {
  return axios
    .post(`/api/v1/create/picture`, data, {
      headers: {
        withCredentials: true,
        'Content-Type': 'multipart/form-data'
      }
    })
    .then(function (response) {
      return response.data;
    })
    .catch(err => {
      if (err.response.data) {
        if (
          err.response.data.error &&
          err.response.data.error === 'Missing cookie access_token_cookie'
        ) {
          return 'Not logged in!';
        } else return err.response.data;
      } else return err.response;
    });
};

/**
 * 日時又は日付のフォーマット
 * @param datetime
 * @param format
 * @param timezone
 */
export const formatDatetime = (
  datetime: any,
  format: string,
  timezone?: string
) => {
  if (timezone === '日本標準時') datetime.setHours(datetime.getHours() + 9); // 協定世界時を日本標準時に変換
  format = format.replace(/YYYY/, datetime.getFullYear());
  format = format.replace(/MM/, ('0' + (datetime.getMonth() + 1)).slice(-2));
  format = format.replace(/DD/, ('0' + datetime.getDate()).slice(-2));
  format = format.replace(/HH/, ('0' + datetime.getHours()).slice(-2));
  format = format.replace(/MM/, ('0' + datetime.getMinutes()).slice(-2));
  format = format.replace(/SS/, ('0' + datetime.getMinutes()).slice(-2));

  return format;
};

/**
 * データサイズのフォーマット
 * @param size
 */
export const formatDataSize = (size: number) => {
  const kb = 1000;
  const mb = Math.pow(kb, 2);
  const gb = Math.pow(kb, 3);
  const tb = Math.pow(kb, 4);

  let target = null;
  let unit = 'byte';

  if (size >= tb) {
    target = tb;
    unit = 'TB';
  } else if (size >= mb) {
    target = mb;
    unit = 'MB';
  } else if (size >= kb) {
    target = kb;
    unit = 'KB';
  }
  const res = target !== null ? Math.floor((size / target) * 100) / 100 : size;
  return res + unit;
};

/**
 * 保存可能なファイル形式の文字列 (ファイル)
 * @returns
 */
export const acceptableFileTypeStr = () => {
  return Object.keys(ACCEPTABLE_FILE_TYPES).join(', ');
};

/**
 * 保存可能なファイル形式の文字列 (写真)
 * @returns
 */
export const acceptablePictureTypeStr = () => {
  return Object.keys(ACCEPTABLE_PICTURE_TYPES).join(', ');
};

/**
 * 保存可能なファイルのContent-Type
 * @returns
 */
export const acceptableFileContentTypes = () => {
  return Array.from(new Set(Object.values(ACCEPTABLE_FILE_TYPES).flat()));
};

/**
 * 保存可能な写真のContent-Type
 * @returns
 */
export const acceptablePictureContentTypes = () => {
  return Array.from(new Set(Object.values(ACCEPTABLE_PICTURE_TYPES).flat()));
};

export const createParams = async (val: any) => {
  let param = '';

  Object.keys(val).forEach((key: string) => {
    if (val[key]) {
      param += `&${key}=${val[key]}`;
    }
  });

  return param;
};

/**
 * ファイルを読み込む
 */
export const loadFile = async (file: File, onload: (result: any) => void) => {
  if (file.type === 'image/heic' || file.type === 'image/heif') {
    const convertedBlob = await heic2any({ blob: file, toType: 'image/jpeg' });
    const reader = new FileReader();
    reader.onload = () => onload(reader.result);
    reader.readAsDataURL(convertedBlob as Blob);
  } else {
    const reader = new FileReader();
    reader.onload = () => onload(reader.result);
    reader.readAsDataURL(file);
  }
};

/**
 * レスポンスデータのチェック
 * @param data
 * @param setErrorMessage
 * @param setOpen
 */
export const checkResData = async (
  data: any,
  enqueueSnackbar: any,
  changeLoggedInData?: (newData: string) => void
) => {
  if (typeof data === 'string') {
    if (data === 'Not logged in!') {
      changeLoggedInData
        ? changeLoggedInData(data)
        : enqueueSnackbar(data, { variant: 'error' });
    } else {
      enqueueSnackbar(data, { variant: 'error' });
    }
  } else {
    if (data.error) {
      if (Array.isArray(data.error)) {
        data.error.map((msg: string) => {
          enqueueSnackbar(msg, { variant: 'error' });
        });
      } else {
        enqueueSnackbar(data.error, { variant: 'error' });
      }
    } else {
      if (data.message) {
        enqueueSnackbar(data.message, { variant: 'success' });
      }

      return data;
    }
  }
};

/**
 * 電子契約のレスポンスデータのチェック
 * @param data
 * @param setErrorMessage
 * @param setOpen
 */
export const checkEContractResData = async (
  data: any,
  enqueueSnackbar: any,
  changeLoggedInData?: (newData: string) => void
) => {
  if (typeof data === 'string') {
    if (data === 'Not logged in!') {
      changeLoggedInData
        ? changeLoggedInData(data)
        : enqueueSnackbar(data, { variant: 'error' });
    } else {
      enqueueSnackbar(data, { variant: 'error' });
    }
  } else {
    if (data.error) {
      if (Array.isArray(data.error)) {
        data.error.map((msg: string) => {
          enqueueSnackbar(msg, { variant: 'error' });
        });
      } else {
        enqueueSnackbar(data.error, { variant: 'error' });
      }
    } else {
      return data;
    }
  }
};

/**
 * 日付が本日以降か判定
 * @param date
 */
export const isTodayAfter = (date: string) => {
  if (date === '') return true;
  const today = new Date().toDateString(); // 本日の日付を取得 例： "Tue Sep 27 2022"
  return new Date(date) >= new Date(today);
};

/**
 * 消費税計算
 * @param price
 * @param taxRate
 * @param taxRule
 */
export const calcTax = (price: number, taxRate: number, taxRule: number) => {
  switch (taxRule) {
    // 切り捨て
    case TAX_RULE.FLOOR:
      return Math.floor(price * taxRate);
    // 切り上げ
    case TAX_RULE.CEIL:
      return Math.ceil(price * taxRate);
    // 四捨五入
    case TAX_RULE.ROUND:
      return Math.round(price * taxRate);
    default:
      console.log('taxRule not found.');
      return Math.floor(price * taxRate);
  }
};

/**
 * 工事一覧（セレクトボックス）取得
 * @param setSatate
 * @param enqueueSnackbar
 */
export const getConstructionsSelectBoxData = (
  setSatate: React.Dispatch<React.SetStateAction<TSelectItem[]>>,
  enqueueSnackbar: any,
  UnselectedItem: TSelectItem
) => {
  getConstructions().then((resData: any) => {
    checkResData(resData, enqueueSnackbar).then((data: any) => {
      // 初期値設定
      let list: TSelectItem[] = [UnselectedItem];
      const listData = data.data;
      // データが存在する場合
      if (listData && listData.length > 0) {
        // 取得したデータを追加
        list = [...list, ...listData];
      }
      // 一覧データを設定
      setSatate(list);
    });
  });
};

/**
 * 入力値がnumber型であり、かつNaNではないことを確かめる
 * @param value
 * @returns
 */
export const isValidNumber = (value: any) => {
  return typeof value === 'number' && !isNaN(value);
};
